<script setup lang="ts">
import { computed, ref } from 'vue'
import { api, type AgentFindings, type AgentFindingDay } from '../../api/client'
import { useAgentFindings } from '../../composables/useAgentFindings'
import AgentDayView from './AgentDayView.vue'

const props = withDefaults(defineProps<{ findings: AgentFindings; canAck?: boolean }>(), { canAck: false })
const latest = computed(() => props.findings.days[0] ?? null)
// Previous 2 run days, collapsed under the latest (Paolo 2026-10-03).
const history = computed(() => props.findings.days.slice(1, 3))

// Acknowledge / withdraw (Paolo 2026-10-16): write, then re-read the shared findings
// store so the Dashboard pills and the tab change together.
const { load } = useAgentFindings()
const busy = ref(false)
const error = ref<string | null>(null)

async function run(op: () => Promise<unknown>) {
  busy.value = true
  error.value = null
  try {
    await op()
    await load()
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
  } finally {
    busy.value = false
  }
}

function acknowledge(day: AgentFindingDay, note: string, reference: string | null) {
  void run(() => api.devopsAcknowledge(props.findings.agent, day.date, { note, reference }))
}

function withdraw(day: AgentFindingDay) {
  void run(() => api.devopsUnacknowledge(props.findings.agent, day.date))
}
</script>

<template>
  <section class="card agent-card" :data-testid="`agent-card-${findings.agent}`">
    <h2>{{ findings.label }}</h2>
    <div v-if="!findings.available" class="muted" data-testid="agent-unavailable">
      Agent findings unavailable — {{ findings.reason }}
    </div>
    <div v-else-if="!latest" class="muted" data-testid="agent-unavailable">No agent runs recorded yet</div>
    <AgentDayView v-else :day="latest" :can-ack="canAck" :busy="busy" @acknowledge="acknowledge" @withdraw="withdraw" />
    <div v-if="error" class="ack-error" data-testid="agent-ack-error">Could not save the acknowledgement — {{ error }}</div>
    <details v-if="history.length" class="history" data-testid="agent-history">
      <summary>History ({{ history.length }} days)</summary>
      <AgentDayView v-for="d in history" :key="d.date" :day="d" />
    </details>
  </section>
</template>

<style scoped>
.agent-card h2 { margin: 0 0 0.25rem; }
.muted { color: #9e9e9e; }
.ack-error { color: #f87171; font-size: 0.9em; margin-top: 0.25rem; }
.history { margin-top: 0.5rem; border-top: 1px solid #ffffff1a; padding-top: 0.5rem; }
.history summary { cursor: pointer; color: #9e9e9e; }
</style>
